import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Input,
  Badge,
  Modal,
  Form as AntForm,
  Switch,
  Select,
  Drawer,
  Tag,
  Tooltip,
  message,
  Spin,
  Empty,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { DateRangePicker } from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import { useNotification } from "../../context/NotificationContext";

/**
 * Production-ready Offers/Coupons Management Page
 * - Uses DateRangePicker (react-bootstrap-daterangepicker)
 * - Right full-height drawer with sticky footer
 * - Validates discount_type to avoid "discount type invalid"
 * - Room types selector handles jsonb / array / string
 */

const DISCOUNT_TYPES = [
  { value: "percent", label: "Percentage (%)" },
  { value: "flat", label: "Flat Amount" },
  { value: "free_nights", label: "Free Nights" },
];

const DEFAULT_FORM = {
  id: null,
  name: "",
  coupon_code: "",
  description: "",
  is_active: true,

  discount_type: "percent", // percent | flat | free_nights
  discount_value: 10, // percent or flat amount
  max_discount_amount: null, // optional for percent

  min_nights: 1,
  min_booking_amount: 0,

  usage_limit_total: null,
  usage_limit_per_guest: null,

  applies_to: "all", // all | room_types
  room_type_ids: [], // array of ids

  start_at: moment().startOf("month").format("YYYY-MM-DD"),
  end_at: moment().format("YYYY-MM-DD"),

  // optional fields you might have
  // stackable: false,
};

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeRoomTypeIds = (raw) => {
  // supports: array [1,2], jsonb objects, stringified json, comma string
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((x) => Number(x)).filter(Boolean);

  if (typeof raw === "string") {
    const s = raw.trim();
    // JSON array string
    if (s.startsWith("[") && s.endsWith("]")) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          return parsed.map((x) => Number(x)).filter(Boolean);
        }
      } catch (e) {
        // ignore
      }
    }
    // comma-separated
    return s
      .split(",")
      .map((x) => Number(String(x).trim()))
      .filter(Boolean);
  }

  // jsonb object scenario: { ids: [1,2] } or { room_type_ids: [...] }
  if (typeof raw === "object") {
    const possible =
      raw.ids || raw.room_type_ids || raw.roomTypes || raw.room_type_id;
    if (Array.isArray(possible)) {
      return possible.map((x) => Number(x)).filter(Boolean);
    }
    if (possible != null) return [Number(possible)].filter(Boolean);
  }

  return [];
};

const normalizeDiscountType = (t) => {
  const v = String(t || "").toLowerCase().trim();
  if (v === "percentage" || v === "pct") return "percent";
  if (v === "amount" || v === "fixed") return "flat";
  if (v === "free" || v === "freenights" || v === "free-night") return "free_nights";
  if (["percent", "flat", "free_nights"].includes(v)) return v;
  return "percent";
};

const BeOffers = () => {
  const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [offers, setOffers] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);

  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({ ...DEFAULT_FORM });


    const { notify } = useNotification();


  const [dateRange, setDateRange] = useState({
    startDate: moment(DEFAULT_FORM.start_at),
    endDate: moment(DEFAULT_FORM.end_at),
  });

  const authHeaders = useMemo(
    () => ({
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const ensureAuth = () => {
    if (!apiUrl || !token) {
      notify("error","Missing API URL or token. Please login again.");
      return false;
    }
    return true;
  };

const fetchAll = async () => {
  if (!ensureAuth()) return;

  setLoading(true);
  try {
    const [offersRes, roomTypesRes] = await Promise.all([
      axios.get(`${apiUrl}/booking-engine/offers`, authHeaders),

      // ✅ use PMS room types endpoint (replace with your correct one)
      axios
        .get(`${apiUrl}/roomtype/listings/`, authHeaders)
        .catch(() => ({ data: { roomTypes: [] } })),
    ]);

    if (!offersRes.data?.success) {
      throw new Error(offersRes.data?.message || "Failed to load offers.");
    }

    // ✅ FIX: API returns data.items
    const rawOffers = offersRes.data?.data?.items || [];

    // ✅ FIX: room types response is { roomTypes: [] }
    const rawRoomTypes =
      roomTypesRes.data?.roomTypes ||
      roomTypesRes.data?.data?.roomTypes ||
      roomTypesRes.data?.data ||
      [];

    // ✅ Normalize room types for lookup
    const rtList = (Array.isArray(rawRoomTypes) ? rawRoomTypes : []).map((rt) => ({
      id: Number(rt.room_type_id ?? rt.id),
      name: rt.type_name ?? rt.name,
    }));
    setRoomTypes(rtList);

    // ✅ Normalize offers so columns can work without changing all render logic
    const offersList = (Array.isArray(rawOffers) ? rawOffers : []).map((o) => ({
      ...o,

      // For your UI expectations:
      coupon_code: o.code || o.coupon_code || o.couponCode || "",
      is_active: String(o.status || "").toUpperCase() === "ACTIVE",

      // Normalize usage fields for your column
      usage_count: Number(o.usage_used || 0),
      usage_limit_total:
        o.usage_limit_global === 0 || o.usage_limit_global == null
          ? null
          : Number(o.usage_limit_global),

      // Ensure room_type_ids always array
      room_type_ids: Array.isArray(o.room_type_ids)
        ? o.room_type_ids
        : (typeof o.room_type_ids === "string"
            ? JSON.parse(o.room_type_ids || "[]")
            : []),
    }));

    setOffers(offersList);
  } catch (err) {
    console.error(err);
    notify("error",err.message || "Failed to load offers.");
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roomTypeOptions = useMemo(
    () =>
      roomTypes
        .filter((rt) => rt?.id && rt?.name)
        .map((rt) => ({ value: rt.id, label: rt.name })),
    [roomTypes]
  );

  const filteredOffers = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    return offers
      .filter((o) => {
        if (!o) return false;
        if (onlyActive && !o.is_active) return false;
        if (!q) return true;
        const hay = [
          o.name,
          o.coupon_code,
          o.description,
          o.discount_type,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [offers, search, onlyActive]);

  const openCreate = () => {
    const initial = { ...DEFAULT_FORM };
    setForm(initial);
    setDateRange({
      startDate: moment(initial.start_at),
      endDate: moment(initial.end_at),
    });
    setDrawerOpen(true);
  };

  const openEdit = (offer) => {
    const normalized = {
      ...DEFAULT_FORM,
      ...offer,
      id: offer.id,
      discount_type: normalizeDiscountType(offer.discount_type),
      room_type_ids: normalizeRoomTypeIds(offer.room_type_ids),
      start_at: offer.start_at
        ? moment(offer.start_at).format("YYYY-MM-DD")
        : DEFAULT_FORM.start_at,
      end_at: offer.end_at
        ? moment(offer.end_at).format("YYYY-MM-DD")
        : DEFAULT_FORM.end_at,
      is_active: typeof offer.is_active === "boolean" ? offer.is_active : true,
    };

    setForm(normalized);
    setDateRange({
      startDate: moment(normalized.start_at),
      endDate: moment(normalized.end_at),
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSaving(false);
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Offer name is required.";
    if (!form.coupon_code.trim()) return "Coupon code is required.";

    const code = form.coupon_code.trim();
    if (!/^[A-Z0-9_-]{3,30}$/i.test(code)) {
      return "Coupon code should be 3–30 chars (A-Z, 0-9, _ or -).";
    }

    const dtype = normalizeDiscountType(form.discount_type);
    if (!["percent", "flat", "free_nights"].includes(dtype)) {
      return "Invalid discount type.";
    }

    if (dtype === "percent") {
      const v = safeNumber(form.discount_value, 0);
      if (v <= 0 || v > 100) return "Percentage must be between 1 and 100.";
      if (form.max_discount_amount != null && form.max_discount_amount !== "") {
        const mx = safeNumber(form.max_discount_amount, -1);
        if (mx < 0) return "Max discount amount must be >= 0.";
      }
    }

    if (dtype === "flat") {
      const v = safeNumber(form.discount_value, 0);
      if (v <= 0) return "Flat amount must be greater than 0.";
    }

    if (dtype === "free_nights") {
      const v = safeNumber(form.discount_value, 0);
      if (v <= 0) return "Free nights must be at least 1.";
      if (safeNumber(form.min_nights, 0) < v) {
        return "Min nights must be >= free nights.";
      }
    }

    if (form.applies_to === "room_types") {
      const ids = normalizeRoomTypeIds(form.room_type_ids);
      if (!ids.length) return "Select at least one room type.";
    }

    // Date validation
    const s = moment(form.start_at, "YYYY-MM-DD", true);
    const e = moment(form.end_at, "YYYY-MM-DD", true);
    if (!s.isValid() || !e.isValid()) return "Invalid validity dates.";
    if (s.isAfter(e)) return "Start date cannot be after end date.";

    return null;
  };


  const toBackendDiscountType = (uiType) => {
  const t = normalizeDiscountType(uiType); // percent|flat|free_nights
  if (t === "percent") return "PERCENTAGE";
  if (t === "flat") return "FLAT";
  return "NIGHT_BASED"; // free_nights
};

const toBackendStatus = (isActive) => (isActive ? "ACTIVE" : "INACTIVE");

const toIsoStart = (yyyyMmDd) =>
  moment(yyyyMmDd, "YYYY-MM-DD").startOf("day").toISOString();

const toIsoEnd = (yyyyMmDd) =>
  moment(yyyyMmDd, "YYYY-MM-DD").endOf("day").toISOString();


const buildPayload = () => {
  const uiType = normalizeDiscountType(form.discount_type); // percent|flat|free_nights
  const backendType = toBackendDiscountType(uiType);

  const discountValue = safeNumber(form.discount_value, 0);

  // keep optional values in rules so backend can store without schema changes
  const rules = {
    max_discount_amount:
      form.max_discount_amount === "" || form.max_discount_amount == null
        ? null
        : safeNumber(form.max_discount_amount, 0),
    applies_to: form.applies_to,
  };

  const payload = {
    name: form.name.trim(),
    coupon_code: form.coupon_code.trim().toUpperCase(),
    description: (form.description || "").trim(),

    // backend expects status, not is_active
    status: toBackendStatus(!!form.is_active),

    // backend expects enum like PERCENTAGE/FLAT/NIGHT_BASED
    discount_type: backendType,

    // for NIGHT_BASED, backend expects free_nights (NOT discount_value)
    discount_value: backendType === "NIGHT_BASED" ? 0 : discountValue,
    free_nights: backendType === "NIGHT_BASED" ? Math.max(0, parseInt(discountValue, 10) || 0) : 0,

    currency_code: "INR", // or take from UI if you add it

    min_nights: Math.max(0, parseInt(form.min_nights || 0, 10) || 0),
    min_amount: safeNumber(form.min_booking_amount, 0), // rename

    usage_limit_global:
      form.usage_limit_total === "" || form.usage_limit_total == null
        ? 0
        : Math.max(0, parseInt(form.usage_limit_total, 10) || 0),

    usage_limit_per_guest:
      form.usage_limit_per_guest === "" || form.usage_limit_per_guest == null
        ? 0
        : Math.max(0, parseInt(form.usage_limit_per_guest, 10) || 0),

    room_type_ids:
      form.applies_to === "room_types"
        ? normalizeRoomTypeIds(form.room_type_ids)
        : [],

    // recommend ISO to avoid timezone boundary issues
    start_at: toIsoStart(form.start_at),
    end_at: toIsoEnd(form.end_at),

    // optional backend fields (safe defaults)
    stackable: false,
    first_time_only: false,
    channels: [],

    // backend stores rules jsonb already
    rules,
  };

  return payload;
};



  const getApiErrorMessage = (err, fallback = "Save failed.") => {
  // 1) axios error with response body
  const data = err?.response?.data;

  // Common shapes:
  // { message: "..." }
  // { error: "..." }
  // { success:false, message:"..." }
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data?.error === "string" && data.error.trim()) return data.error;

  // sometimes message is nested
  if (typeof data?.data?.message === "string" && data.data.message.trim()) return data.data.message;

  // array errors
  if (Array.isArray(data?.errors) && data.errors.length) {
    const e0 = data.errors[0];
    if (typeof e0 === "string") return e0;
    if (typeof e0?.message === "string") return e0.message;
    if (typeof e0?.msg === "string") return e0.msg;
  }

  // 2) thrown Error("...") in code
  if (typeof err?.message === "string" && err.message.trim()) return err.message;

  return fallback;
};

const saveOffer = async () => {
  const error = validateForm();
  if (error) return notify("error", error);

  if (!ensureAuth()) return;

  setSaving(true);
  try {
    const payload = buildPayload();

    const url = form.id
      ? `${apiUrl}/booking-engine/offers/${form.id}`
      : `${apiUrl}/booking-engine/offers`;

    const res = form.id
      ? await axios.put(url, payload, authHeaders)
      : await axios.post(url, payload, authHeaders);

    // If backend uses success flag even on 200, handle it here:
    if (res?.data?.success === false) {
      // throw Error so we unify handling in catch
      throw new Error(res.data?.message || "Failed to save offer.");
    }

    notify("success", form.id ? "Offer updated." : "Offer created.");
    closeDrawer();
    fetchAll();
  } catch (err) {
    console.error(err);
    notify("error", getApiErrorMessage(err, "Save failed."));
  } finally {
    setSaving(false);
  }
};


  const deleteOffer = async (offer) => {
    if (!ensureAuth()) return;

    Modal.confirm({
      title: "Delete this offer?",
      content: `This will permanently delete "${offer?.name || "Offer"}".`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await axios.delete(
            `${apiUrl}/booking-engine/offers/${offer.id}`,
            authHeaders
          );
          if (!res.data?.success) {
            throw new Error(res.data?.message || "Failed to delete offer.");
          }
          notify("success","Offer deleted.");
          fetchAll();
        } catch (err) {
          console.error(err);
          notify("error",
            err.response?.data?.message || err.message || "Delete failed."
          );
        }
      },
    });
  };

  const renderValidityPicker = () => (
    <DateRangePicker
      initialSettings={{
        singleDatePicker: false,
        showDropdowns: true,
        autoApply: true,
        opens: "left",
        startDate: dateRange.startDate.toDate(),
        endDate: dateRange.endDate.toDate(),
        locale: {
          format: "DD MMM YYYY",
          applyLabel: "Apply",
          cancelLabel: "Cancel",
        },
        ranges: {
          Today: [moment().toDate(), moment().toDate()],
          "This Month": [
            moment().startOf("month").toDate(),
            moment().endOf("month").toDate(),
          ],
          "Next 7 Days": [
            moment().toDate(),
            moment().add(6, "days").toDate(),
          ],
          "Next 30 Days": [
            moment().toDate(),
            moment().add(29, "days").toDate(),
          ],
        },
      }}
      onCallback={(start, end) => {
        const s = moment(start).startOf("day");
        const e = moment(end).endOf("day");

        setDateRange({ startDate: s, endDate: e });

        // keep payload safe (YYYY-MM-DD)
        setForm((prev) => ({
          ...prev,
          start_at: s.format("YYYY-MM-DD"),
          end_at: e.format("YYYY-MM-DD"),
        }));
      }}
    >
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          readOnly
          value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
          style={{
            background: "#fff",
            fontWeight: 700,
            textAlign: "center",
            cursor: "pointer",
            height: 40,
            borderRadius: 10,
          }}
        />
        <span className="input-group-text bg-white" style={{ borderRadius: "0 10px 10px 0" }}>
          <i className="fa fa-calendar" />
        </span>
      </div>
    </DateRangePicker>
  );

  const columns = [
    {
      title: "Offer",
      dataIndex: "name",
      key: "name",
      render: (_, r) => (
        <div style={{ minWidth: 240 }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>
            {r.name}{" "}
            {!r.is_active ? (
              <Tag color="default" style={{ marginLeft: 6 }}>INACTIVE</Tag>
            ) : (
              <Tag color="green" style={{ marginLeft: 6 }}>ACTIVE</Tag>
            )}
          </div>
          <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>
            Code: <span style={{ fontWeight: 700 }}>{(r.coupon_code || "").toUpperCase()}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, r) => {
        const t = normalizeDiscountType(r.discount_type);
        const val = safeNumber(r.discount_value, 0);
        if (t === "percent") return <Tag color="blue">{val}% OFF</Tag>;
        if (t === "flat") return <Tag color="purple">₹{val} OFF</Tag>;
        if (t === "free_nights") return <Tag color="gold">{val} Free Night(s)</Tag>;
        return <Tag>—</Tag>;
      },
    },
    {
      title: "Validity",
      key: "validity",
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div><b>{r.start_at ? moment(r.start_at).format("DD MMM YYYY") : "—"}</b></div>
          <div style={{ color: "#6b7280" }}>
            to <b>{r.end_at ? moment(r.end_at).format("DD MMM YYYY") : "—"}</b>
          </div>
        </div>
      ),
    },
    {
      title: "Applies To",
      key: "applies_to",
      render: (_, r) => {
        const applies = r.applies_to || "all";
        if (applies === "all") return <Tag color="cyan">All Room Types</Tag>;
        const ids = normalizeRoomTypeIds(r.room_type_ids);
        return (
          <Tooltip
            title={
              ids.length
                ? ids
                    .map((id) => roomTypes.find((x) => x.id === id)?.name || `#${id}`)
                    .join(", ")
                : "—"
            }
          >
            <Tag color="geekblue">{ids.length} Room Type(s)</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Usage",
      key: "usage",
      render: (_, r) => {
        // usage counter can come from API (e.g. usage_count) — optional
const used = safeNumber(r.usage_used ?? r.usage_count, 0);
const limitRaw = r.usage_limit_global ?? r.usage_limit_total;
const limit = !limitRaw || Number(limitRaw) === 0 ? null : safeNumber(limitRaw, 0);

        return (
          <div style={{ fontSize: 12 }}>
            <div>
              <b>{used}</b> used{" "}
              {limit != null ? (
                <span style={{ color: "#6b7280" }}> / {limit}</span>
              ) : (
                <span style={{ color: "#6b7280" }}> / ∞</span>
              )}
            </div>
            {limit != null && (
              <Badge
                status={used >= limit ? "error" : "processing"}
                text={used >= limit ? "Limit reached" : "Available"}
              />
            )}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 160,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => deleteOffer(r)} />
        </div>
      ),
    },
  ];

  // Drawer left image panel (premium, full height, index)
  const DrawerHero = () => {
    const slides = [
      {
        title: "Boost direct bookings",
        desc: "Launch member-only coupons & seasonal promotions in seconds.",
      },
      {
        title: "Smart controls",
        desc: "Limits, validity, room-type rules — everything auditable.",
      },
      {
        title: "Revenue-first",
        desc: "Keep discounts under control with caps & minimum nights.",
      },
    ];

    const [idx, setIdx] = useState(0);

    useEffect(() => {
      const t = setInterval(() => setIdx((p) => (p + 1) % slides.length), 3500);
      return () => clearInterval(t);
    }, []);

    return (
      <div
        style={{
          height: "100%",
          padding: 22,
          color: "#fff",
          position: "relative",
          background:
            "radial-gradient(circle at 15% 15%, rgba(255,255,255,0.18), transparent 55%), linear-gradient(135deg, #0b1220, #1b2a52 60%, #0b1220)",
          overflow: "hidden",
          borderRadius: 16,
        }}
      >
        <div style={{ position: "absolute", inset: 0, opacity: 0.35, background: "radial-gradient(circle at 80% 20%, rgba(56,189,248,0.55), transparent 50%)" }} />

        <div style={{ position: "relative", zIndex: 2, height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.9 }}>
            Booking Engine • Offers
          </div>

          <div style={{ marginTop: 14, fontSize: 26, fontWeight: 900, lineHeight: 1.15 }}>
            {slides[idx].title}
          </div>
          <div style={{ marginTop: 10, opacity: 0.92, fontSize: 14, maxWidth: 340 }}>
            {slides[idx].desc}
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {slides.map((_, i) => (
              <div
                key={i}
                onClick={() => setIdx(i)}
                style={{
                  height: 8,
                  width: i === idx ? 22 : 8,
                  borderRadius: 999,
                  cursor: "pointer",
                  background: i === idx ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.35)",
                  transition: "all .25s ease",
                }}
              />
            ))}
            <div style={{ marginLeft: "auto", fontSize: 12, opacity: 0.85 }}>
              {idx + 1}/{slides.length}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DiscountValueLabel = useMemo(() => {
    const dt = normalizeDiscountType(form.discount_type);
    if (dt === "percent") return "Discount %";
    if (dt === "flat") return "Discount Amount";
    return "Free Nights";
  }, [form.discount_type]);

  if (loading) {
    return (
      <div style={{ height: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        <div className="page-header page-top-icons" style={{ marginBottom: 16 }}>
          <div className="row align-items-center">
            <div className="col-md-4">
              <h3 className="page-title" style={{ marginBottom: 0 }}>
                Booking Engine • Offers & Coupons
              </h3>
              <div style={{ color: "#6b7280", fontWeight: 600, marginTop: 4 }}>
                Create, control and track promotional offers.
              </div>
            </div>

            <div className="col-md-8">
              <div className="d-flex justify-content-end align-items-center flex-wrap gap-2">
                <Input
                  placeholder="Search by offer name / code"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 320 }}
                  allowClear
                />
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: "#6b7280", fontWeight: 700 }}>Active only</span>
                  <Switch checked={onlyActive} onChange={setOnlyActive} />
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Create Offer
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: 16,
            overflow: "hidden",
            border: "1px solid rgba(15,23,42,0.08)",
          }}
        >
          {filteredOffers.length === 0 ? (
            <div style={{ padding: 40 }}>
              <Empty
                description={
                  <div>
                    <div style={{ fontWeight: 800 }}>No offers found</div>
                    <div style={{ color: "#6b7280" }}>Create your first coupon to boost direct bookings.</div>
                  </div>
                }
              />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredOffers}
              rowKey={(r) => r.id}
              pagination={{ pageSize: 10 }}
              style={{ padding: 0 }}
            />
          )}
        </Card>

        {/* Right Drawer (Full Height, not hidden by top nav) */}
        <Drawer
          open={drawerOpen}
          onClose={closeDrawer}
          width={980}
            zIndex={9999}                      // ✅ above any nav
  maskStyle={{ zIndex: 9998 }}
          closeIcon={null}
          styles={{
              header: { display: "none" },
              body: { padding: 0 },
              mask: { zIndex: 9998 },
              content: { zIndex: 9999 },
              wrapper: { zIndex: 9999 },
            }}
          placement="right"
          destroyOnClose
          maskClosable={!saving}
        >
          <div style={{ height: "100vh", display: "flex" }}>
            {/* Left image panel */}
            <div style={{ width: 360, padding: 16, background: "#0b1220" }}>
              <DrawerHero />
            </div>

            {/* Right form */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#fff" }}>
              {/* Top bar */}
              <div
                style={{
                  padding: "18px 22px",
                  borderBottom: "1px solid rgba(15,23,42,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 18, fontWeight: 900 }}>
                    {form.id ? "Edit Offer" : "Create Offer"}
                  </div>
                  <div style={{ color: "#6b7280", fontWeight: 600, marginTop: 2 }}>
                    Set rules, validity and limits. Always controlled.
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: "#6b7280", fontWeight: 800 }}>Active</span>
                  <Switch
                    checked={!!form.is_active}
                    onChange={(v) => setForm((p) => ({ ...p, is_active: v }))}
                  />
                  <Button onClick={closeDrawer} disabled={saving}>
                    Close
                  </Button>
                </div>
              </div>

              {/* Scroll area */}
              <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <label style={styles.label}>Offer Name</label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="E.g., Winter Deal"
                      size="large"
                    />
                  </Col>

                  <Col span={12}>
                    <label style={styles.label}>Coupon Code</label>
                    <Input
                      value={form.coupon_code}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          coupon_code: e.target.value.toUpperCase(),
                        }))
                      }
                      placeholder="E.g., WINTER10"
                      size="large"
                    />
                    <div style={{ color: "#6b7280", marginTop: 6, fontSize: 12, fontWeight: 600 }}>
                      Use A–Z / 0–9 / _ / - (3–30 chars).
                    </div>
                  </Col>

                  <Col span={24} style={{ marginTop: 12 }}>
                    <label style={styles.label}>Description</label>
                    <Input.TextArea
                      value={form.description}
                      onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Short text shown in admin for reference"
                      rows={3}
                    />
                  </Col>

                  <Col span={24} style={{ marginTop: 18 }}>
                    <Card
                      style={styles.sectionCard}
                      title={<span style={styles.sectionTitle}>Discount Rules</span>}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <label style={styles.label}>Discount Type</label>
                          <Select
                            value={normalizeDiscountType(form.discount_type)}
                            onChange={(v) =>
                              setForm((p) => ({
                                ...p,
                                discount_type: v,
                              }))
                            }
                            options={DISCOUNT_TYPES}
                            size="large"
                            style={{ width: "100%" }}
                          />
                        </Col>

                        <Col span={8}>
                          <label style={styles.label}>{DiscountValueLabel}</label>
                          <Input
                            size="large"
                            value={form.discount_value}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                discount_value: e.target.value,
                              }))
                            }
                            type="number"
                            min={0}
                            placeholder={form.discount_type === "percent" ? "10" : "500"}
                          />
                        </Col>

                        <Col span={8}>
                          <label style={styles.label}>
                            Max Discount Amount{" "}
                            <span style={{ color: "#6b7280", fontWeight: 700 }}>(optional)</span>
                          </label>
                          <Input
                            size="large"
                            value={form.max_discount_amount ?? ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                max_discount_amount: e.target.value,
                              }))
                            }
                            type="number"
                            min={0}
                            disabled={normalizeDiscountType(form.discount_type) !== "percent"}
                            placeholder="E.g., 2000"
                          />
                        </Col>
                      </Row>

                      <Row gutter={16} style={{ marginTop: 14 }}>
                        <Col span={8}>
                          <label style={styles.label}>Min Nights</label>
                          <Input
                            size="large"
                            value={form.min_nights}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                min_nights: e.target.value,
                              }))
                            }
                            type="number"
                            min={0}
                          />
                        </Col>

                        <Col span={8}>
                          <label style={styles.label}>Min Booking Amount</label>
                          <Input
                            size="large"
                            value={form.min_booking_amount}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                min_booking_amount: e.target.value,
                              }))
                            }
                            type="number"
                            min={0}
                          />
                        </Col>

                        <Col span={8}>
                          <label style={styles.label}>Validity</label>
                          {renderValidityPicker()}
                        </Col>
                      </Row>
                    </Card>
                  </Col>

                  <Col span={24} style={{ marginTop: 14 }}>
                    <Card
                      style={styles.sectionCard}
                      title={<span style={styles.sectionTitle}>Applicability</span>}
                    >
                      <Row gutter={16}>
                        <Col span={10}>
                          <label style={styles.label}>Applies To</label>
                          <Select
                            size="large"
                            value={form.applies_to}
                            onChange={(v) =>
                              setForm((p) => ({
                                ...p,
                                applies_to: v,
                                room_type_ids: v === "all" ? [] : p.room_type_ids,
                              }))
                            }
                            style={{ width: "100%" }}
                            options={[
                              { value: "all", label: "All Room Types" },
                              { value: "room_types", label: "Selected Room Types" },
                            ]}
                          />
                        </Col>

                        <Col span={14}>
                          <label style={styles.label}>Room Types</label>
                          <Select
                            mode="multiple"
                            size="large"
                            value={normalizeRoomTypeIds(form.room_type_ids)}
                            onChange={(vals) =>
                              setForm((p) => ({
                                ...p,
                                room_type_ids: vals,
                              }))
                            }
                            options={roomTypeOptions}
                            style={{ width: "100%" }}
                            disabled={form.applies_to !== "room_types"}
                            placeholder={
                              form.applies_to !== "room_types"
                                ? "Enable 'Selected Room Types' to choose"
                                : "Select room types…"
                            }
                            showSearch
                            optionFilterProp="label"
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>

                  <Col span={24} style={{ marginTop: 14 }}>
                    <Card
                      style={styles.sectionCard}
                      title={<span style={styles.sectionTitle}>Usage Limits</span>}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <label style={styles.label}>
                            Total Usage Limit{" "}
                            <span style={{ color: "#6b7280", fontWeight: 700 }}>(optional)</span>
                          </label>
                          <Input
                            size="large"
                            value={form.usage_limit_total ?? ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                usage_limit_total: e.target.value,
                              }))
                            }
                            type="number"
                            min={0}
                            placeholder="E.g., 100"
                          />
                        </Col>
                        <Col span={12}>
                          <label style={styles.label}>
                            Usage Per Guest{" "}
                            <span style={{ color: "#6b7280", fontWeight: 700 }}>(optional)</span>
                          </label>
                          <Input
                            size="large"
                            value={form.usage_limit_per_guest ?? ""}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                usage_limit_per_guest: e.target.value,
                              }))
                            }
                            type="number"
                            min={0}
                            placeholder="E.g., 1"
                          />
                        </Col>
                      </Row>

                      <div style={{ marginTop: 10, color: "#6b7280", fontSize: 12, fontWeight: 600 }}>
                        Tip: Keep limits to prevent leakage and preserve ADR.
                      </div>
                    </Card>
                  </Col>
                </Row>
              </div>

              {/* Sticky footer (always visible) */}
              <div
                style={{
                  padding: "14px 22px",
                  borderTop: "1px solid rgba(15,23,42,0.08)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "rgba(255,255,255,0.98)",
                  position: "sticky",
                  bottom: 0,
                  zIndex: 100,
                }}
              >
                <div style={{ color: "#6b7280", fontWeight: 700 }}>
                  {form.id ? "Editing existing offer" : "Creating a new offer"}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Button onClick={closeDrawer} disabled={saving}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={saveOffer} loading={saving}>
                    Save Offer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Drawer>

        <style>{globalCss}</style>
      </div>
    </div>
  );
};

const styles = {
  label: {
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#111827",
    display: "block",
    marginBottom: 6,
  },
  sectionCard: {
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  },
  sectionTitle: {
    fontWeight: 900,
    color: "#0f172a",
  },
};

const globalCss = `
  .ant-drawer-mask { background: rgba(15,23,42,0.45) !important; }
  .ant-drawer-content-wrapper { top: 0 !important; } /* ensures no top-nav overlap */
  .ant-drawer-content { border-radius: 16px 0 0 16px; overflow: hidden; }
  .ant-table-thead > tr > th { font-weight: 900; color: #0f172a; }
`;

export default BeOffers;
